import React from "react";
import { Modal, Spin, Alert } from "antd";
import {
  ReloadOutlined,
  FileTextOutlined,
  CalendarOutlined,
  CheckOutlined,
  CloseCircleOutlined,
  InfoCircleOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import {
  formatWelcomeAiCreditBreakdown,
  resolveQuotaNotApplicableMessage,
} from "util/quotaCheckDisplay";
import welcomeStyles from "pages/Welcome/style.module.scss";

/**
 * Regenerate widget flow: quota check UI matching My Workspace (Welcome).
 */
export function WelcomeWidgetRegenerateQuotaModal({
  open,
  onCancel,
  onConfirm,
  confirmLoading,
  regenerateQuotaLoading,
  regenerateQuotaResult,
  onRecheck,
  widgetDisplayName,
  quotaWidget,
  buildPayload,
  pivotKeyLabel,
}) {
  const { t } = useTranslation();

  const payload = quotaWidget && buildPayload ? buildPayload(quotaWidget) : null;
  const dateRangeText =
    payload?.start_date && payload?.end_date
      ? `${payload.start_date} – ${payload.end_date}`
      : t("welcome.reviewSummaryNotSelected");
  const creditBreakdownText = formatWelcomeAiCreditBreakdown(
    regenerateQuotaResult,
    t,
    { pivotKeyLabel },
  );
  const dashboardText = payload?.dashboard_ids?.length
    ? payload.dashboard_ids
        .map((id) => {
          const fromResult = regenerateQuotaResult?.dashboard_details?.find(
            (d) => String(d.dashboard_id) === String(id),
          )?.dashboard_name;
          if (fromResult) return fromResult;
          return regenerateQuotaLoading ? `#${id}` : `#${id}`;
        })
        .join(", ")
    : t("welcome.reviewSummaryNotSelected");

  return (
    <Modal
      title={t("welcome.regenerateQuotaTitle")}
      open={open}
      onOk={onConfirm}
      onCancel={onCancel}
      okText={t("welcome.regenerate")}
      cancelText={t("common.cancel")}
      width={760}
      destroyOnClose
      confirmLoading={confirmLoading}
      okButtonProps={{
        disabled:
          regenerateQuotaLoading ||
          !regenerateQuotaResult ||
          regenerateQuotaResult?.error === true ||
          regenerateQuotaResult?.sufficient === false ||
          regenerateQuotaResult?.applicable === false ||
          regenerateQuotaResult?.welcome_genai_insufficient_reviews === true,
      }}
    >
      <div className={welcomeStyles.reviewCreateStep}>
        <p className={welcomeStyles.reviewCreateTitle}>{t("welcome.regenerateQuotaSubtitle")}</p>
        <div className={welcomeStyles.requestSummaryCards}>
          <div className={welcomeStyles.requestSummaryCard}>
            <div className={welcomeStyles.requestSummaryCardIcon}>
              <ReloadOutlined />
            </div>
            <div className={welcomeStyles.requestSummaryCardLabel}>
              {t("welcome.reviewSummaryMetric").toUpperCase()}
            </div>
            <div className={welcomeStyles.requestSummaryCardValue}>
              {widgetDisplayName || t("welcome.reviewSummaryNotSelected")}
            </div>
          </div>
          <div className={welcomeStyles.requestSummaryCard}>
            <div className={welcomeStyles.requestSummaryCardIcon}>
              <FileTextOutlined />
            </div>
            <div className={welcomeStyles.requestSummaryCardLabel}>
              {t("welcome.reviewSummaryDashboards").toUpperCase()}
            </div>
            <div className={welcomeStyles.requestSummaryCardValue}>{dashboardText}</div>
          </div>
          <div className={welcomeStyles.requestSummaryCard}>
            <div className={welcomeStyles.requestSummaryCardIcon}>
              <CalendarOutlined />
            </div>
            <div className={welcomeStyles.requestSummaryCardLabel}>
              {t("welcome.reviewSummaryDateRange").toUpperCase()}
            </div>
            <div className={welcomeStyles.requestSummaryCardValue}>{dateRangeText}</div>
          </div>
        </div>

        <p className={welcomeStyles.reviewCreateTitle}>{t("welcome.aiQuotaCheckTitle")}</p>
        {!regenerateQuotaLoading &&
          regenerateQuotaResult?.welcome_genai_insufficient_reviews && (
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 12 }}
              message={t("welcome.quotaGenaiTooFewReviews", {
                count: Number(regenerateQuotaResult.total_review_count) || 0,
                min: regenerateQuotaResult.welcome_genai_minimum_reviews ?? 3,
              })}
            />
          )}

        <div
          className={`${welcomeStyles.quotaCardV2} ${
            regenerateQuotaLoading || !regenerateQuotaResult
              ? welcomeStyles.quotaCardV2Loading
              : regenerateQuotaResult?.sufficient === true
                ? welcomeStyles.quotaCardV2Success
                : regenerateQuotaResult?.sufficient === false
                  ? welcomeStyles.quotaCardV2Error
                  : welcomeStyles.quotaCardV2Warning
          }`}
        >
          {regenerateQuotaLoading ? (
            <div className={welcomeStyles.quotaCardV2Inner}>
              <Spin size="small" />
              <span style={{ marginLeft: 10, color: "#888" }}>
                {t("welcome.checkingQuota")}...
              </span>
            </div>
          ) : !regenerateQuotaResult ? (
            <div className={welcomeStyles.quotaCardV2Inner}>
              <span style={{ color: "#888" }}>{t("welcome.preparingQuotaCheck")}...</span>
            </div>
          ) : regenerateQuotaResult?.sufficient === true ? (
            <>
              <div className={welcomeStyles.quotaCardV2Header}>
                <div className={welcomeStyles.quotaCardV2Status}>
                  <CheckOutlined className={welcomeStyles.quotaCardV2StatusIcon} />
                  <span className={welcomeStyles.quotaCardV2StatusText}>
                    {regenerateQuotaResult?.unlimited
                      ? t("welcome.unlimitedQuota")
                      : t("welcome.quotaVerified")}
                  </span>
                </div>
                <button
                  type="button"
                  className={welcomeStyles.quotaCardV2Recheck}
                  onClick={() => quotaWidget && onRecheck && onRecheck(quotaWidget)}
                >
                  <ReloadOutlined />
                </button>
              </div>
              {!regenerateQuotaResult?.unlimited && (
                <div className={welcomeStyles.quotaCardV2Body}>
                  <div className={welcomeStyles.quotaPillRow}>
                    <div className={welcomeStyles.quotaPillGroup}>
                      <span className={welcomeStyles.quotaPillLabel}>{t("welcome.currentQuota")}</span>
                      <span className={`${welcomeStyles.quotaPill} ${welcomeStyles.quotaPillPurple}`}>
                        {regenerateQuotaResult?.current_quota ?? 0}
                      </span>
                    </div>
                    <span className={welcomeStyles.quotaPillArrow}>→</span>
                    <div className={welcomeStyles.quotaPillGroup}>
                      <span className={welcomeStyles.quotaPillLabel}>{t("welcome.willUse")}</span>
                      <span className={`${welcomeStyles.quotaPill} ${welcomeStyles.quotaPillOrange}`}>
                        {regenerateQuotaResult?.required_amount ?? 0}
                      </span>
                    </div>
                    <span className={welcomeStyles.quotaPillArrow}>→</span>
                    <div className={welcomeStyles.quotaPillGroup}>
                      <span className={welcomeStyles.quotaPillLabel}>
                        {t("welcome.remainingQuota")}
                      </span>
                      <span className={`${welcomeStyles.quotaPill} ${welcomeStyles.quotaPillPurple}`}>
                        {Math.max(
                          0,
                          (regenerateQuotaResult?.remaining_quota ?? 0) -
                            (regenerateQuotaResult?.required_amount ?? 0),
                        )}
                      </span>
                    </div>
                  </div>
                  {creditBreakdownText && (
                    <div className={welcomeStyles.quotaCreditBreakdown}>
                      {creditBreakdownText}
                    </div>
                  )}
                  {Number.isFinite(Number(regenerateQuotaResult?.total_review_count)) && (
                    <div className={welcomeStyles.quotaCardV2Footer}>
                      <span>
                        {t("welcome.quotaScopedReviewCount", {
                          count: Number(regenerateQuotaResult.total_review_count),
                          dashboards: regenerateQuotaResult?.accessible_dashboards_count || 1,
                        })}
                      </span>
                      <button
                        type="button"
                        className={welcomeStyles.quotaRecheckLink}
                        onClick={() => quotaWidget && onRecheck && onRecheck(quotaWidget)}
                      >
                        {t("welcome.recheckQuota")}
                      </button>
                    </div>
                  )}
                  {regenerateQuotaResult?.reset_date && (
                    <div className={welcomeStyles.quotaResetDate}>
                      {t("welcome.quotaResetsOn", {
                        date: new Date(regenerateQuotaResult.reset_date).toLocaleDateString(
                          "tr-TR",
                          {
                            day: "numeric",
                            month: "long",
                            year: "numeric",
                          },
                        ),
                      })}
                    </div>
                  )}
                </div>
              )}
            </>
          ) : regenerateQuotaResult?.sufficient === false ? (
            <>
              <div className={welcomeStyles.quotaCardV2Header}>
                <div className={welcomeStyles.quotaCardV2Status}>
                  <CloseCircleOutlined className={welcomeStyles.quotaCardV2ErrorIcon} />
                  <span
                    className={`${welcomeStyles.quotaCardV2StatusText} ${welcomeStyles.quotaCardV2StatusTextError}`}
                  >
                    {t("welcome.insufficientQuota")}
                  </span>
                </div>
                <button
                  type="button"
                  className={welcomeStyles.quotaCardV2Recheck}
                  onClick={() => quotaWidget && onRecheck && onRecheck(quotaWidget)}
                >
                  <ReloadOutlined />
                </button>
              </div>
              <div className={welcomeStyles.quotaCardV2Body}>
                <div className={welcomeStyles.quotaPillRow}>
                  <div className={welcomeStyles.quotaPillGroup}>
                    <span className={welcomeStyles.quotaPillLabel}>{t("welcome.available")}</span>
                    <span className={`${welcomeStyles.quotaPill} ${welcomeStyles.quotaPillPurple}`}>
                      {regenerateQuotaResult?.current_quota ?? 0}
                    </span>
                  </div>
                  <span className={welcomeStyles.quotaPillArrow}>→</span>
                  <div className={welcomeStyles.quotaPillGroup}>
                    <span className={welcomeStyles.quotaPillLabel}>{t("welcome.required")}</span>
                    <span className={`${welcomeStyles.quotaPill} ${welcomeStyles.quotaPillOrange}`}>
                      {regenerateQuotaResult?.required_amount ?? 0}
                    </span>
                  </div>
                  <span className={welcomeStyles.quotaPillArrow}>→</span>
                  <div className={welcomeStyles.quotaPillGroup}>
                    <span className={welcomeStyles.quotaPillLabel}>{t("welcome.shortfall")}</span>
                    <span className={`${welcomeStyles.quotaPill} ${welcomeStyles.quotaPillRed}`}>
                      -
                      {Math.max(
                        0,
                        (regenerateQuotaResult?.required_amount ?? 0) -
                          (regenerateQuotaResult?.current_quota ?? 0),
                      )}
                    </span>
                  </div>
                </div>
                {creditBreakdownText && (
                  <div className={welcomeStyles.quotaCreditBreakdown}>
                    {creditBreakdownText}
                  </div>
                )}
                <div className={welcomeStyles.quotaInsufficientAction}>
                  <WarningOutlined className={welcomeStyles.quotaInsufficientActionIcon} />
                  <span>{t("welcome.contactAdminForQuota")}</span>
                </div>
              </div>
            </>
          ) : regenerateQuotaResult?.applicable === false ? (
            <div className={welcomeStyles.quotaCardV2Inner}>
              <InfoCircleOutlined style={{ color: "#faad14", fontSize: 16 }} />
              <span style={{ marginLeft: 10 }}>
                {resolveQuotaNotApplicableMessage(regenerateQuotaResult, t)}
              </span>
            </div>
          ) : null}
        </div>
      </div>
    </Modal>
  );
}
